import type { Workspace } from '@up2stay/up2promo-api-client';
import {
  BillingCycle,
  LocaleEnum,
  WorkspacePlan,
  WorkspacesApi,
} from '@up2stay/up2promo-api-client';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { TRIAL_DAYS } from '@/core/billing/plan-catalog';
import { canPurchaseBillingInApp } from '@/core/billing/can-purchase-billing-in-app';
import { EnterpriseContactDialog } from '@/core/components/billing/enterprise-contact-dialog';
import { parseGetStartedSlice } from '@/core/guidance/checklist-persistence';
import { useStartCheckout } from '@/core/hooks/use-billing';
import { useLocale } from '@/core/hooks/use-locale';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import {
  triggerErrorHaptic,
  triggerSelectionHaptic,
  triggerSuccessHaptic,
} from '@/core/lib/dnd-haptics';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { usePendingUpgradeStore } from '@/core/store/pending-upgrade.store';
import { useWorkspaceStore } from '@/core/store/workspace.store';

import {
  stashPendingWorkspaceIntent,
  trackOnboardingIntentSelected,
  type WorkspaceIntent,
} from './onboarding-intent';
import { OnboardingIntentStep } from './onboarding-intent-step';
import {
  invalidatePlanOnboardingGate,
  persistPlanConfirmed,
  setPlanOnboardingGateIncomplete,
} from './onboarding-plan-gate';
import { OnboardingPlanStep } from './onboarding-plan-step';
import { OnboardingSuccessStep } from './onboarding-success-step';
import { OnboardingWorkspaceStep } from './onboarding-workspace-step';
import { WizardShell } from './wizard-shell';
import {
  getGuidanceProgress,
  updateGuidanceProgress,
} from '@/core/guidance/guidance-progress-client';

enum WizardStep {
  INTENT = 1,
  WORKSPACE = 2,
  PLAN = 3,
  SUCCESS = 4,
}

const TOTAL_STEPS = 4;

/**
 * Persist intent on the user×workspace Guidance Progress checklist slice
 * (server source of truth for refresh / other devices). localStorage pending
 * is only a same-device fallback if the API call fails.
 */
async function persistWorkspaceIntent(
  workspaceId: string,
  intent: WorkspaceIntent,
): Promise<void> {
  stashPendingWorkspaceIntent(intent);
  try {
    // Patch only this key — avoids RMW races with planConfirmed / checklist flush.
    await updateGuidanceProgress({
      workspaceId,
      checklists: { intent },
    });
  } catch {
    // Pending stash remains for checklist hydrate fallback on this device.
  }
}

export interface CreateWorkspaceWizardProps {
  /**
   * `onboarding` — first-time/zero-workspace flow. No cancel UI.
   * `create`     — add-another-workspace flow (in-shell page). Dismissible.
   */
  mode: 'onboarding' | 'create';
  /**
   * Called when the user leaves the success step ("Go to dashboard").
   * Receives the newly created workspace.
   */
  onCreated: (workspace: Workspace) => void;
  /**
   * Optional top-right slot inside the wizard chrome (e.g. cancel when the
   * host page does not provide its own header action). Hidden on success.
   */
  topRightAction?: ReactNode;
  /**
   * Visual variant forwarded to the underlying WizardShell:
   * - `page`  for full-page onboarding (floating mobile next button)
   * - `shell` for in-shell create-another (same floating CTA, no extra safe-area)
   * - `dialog` reserved for overlay usage (inline footer everywhere)
   */
  variant?: 'page' | 'dialog' | 'shell';
  /**
   * Resume after reload: workspace exists, intent set, plan not confirmed.
   * Skips Intent/Details and opens on Plan.
   */
  resumeWorkspace?: Workspace | null;
}

export function CreateWorkspaceWizard({
  mode,
  onCreated,
  topRightAction,
  variant = 'page',
  resumeWorkspace = null,
}: CreateWorkspaceWizardProps) {
  const dict = useDictionary();
  const appLocale = useLocale();
  const queryClient = useQueryClient();
  const { refetch } = useWorkspaces();
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  const startCheckout = useStartCheckout();
  const setPendingUpgrade = usePendingUpgradeStore((s) => s.setPending);
  /** Cap native: no Stripe — skip plan step (App Store 3.1.3(f)). */
  const billingInApp = canPurchaseBillingInApp();
  const skipPlanStep = !billingInApp;
  const totalSteps = skipPlanStep ? 3 : TOTAL_STEPS;

  const [currentStep, setCurrentStep] = useState<WizardStep>(() =>
    resumeWorkspace
      ? billingInApp
        ? WizardStep.PLAN
        : WizardStep.SUCCESS
      : WizardStep.INTENT,
  );
  const [direction, setDirection] = useState(1);
  const [intent, setIntent] = useState<WorkspaceIntent | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<WorkspacePlan | null>(null);
  const [billingCycle, setBillingCycle] = useState(BillingCycle.MONTHLY);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);

  const [workspaceName, setWorkspaceName] = useState(
    () => resumeWorkspace?.name ?? '',
  );
  const [workspaceUsername, setWorkspaceUsername] = useState(
    () => resumeWorkspace?.username ?? '',
  );
  const [workspaceDescription, setWorkspaceDescription] = useState('');
  const [workspacePhotoFileId, setWorkspacePhotoFileId] = useState<
    string | undefined
  >();
  const [workspacePhotoPreview, setWorkspacePhotoPreview] = useState<
    string | undefined
  >();
  const [isCreating, setIsCreating] = useState(false);
  const [isConfirmingPlan, setIsConfirmingPlan] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdWorkspace, setCreatedWorkspace] = useState<Workspace | null>(
    () => resumeWorkspace,
  );

  const isSuccessStep = currentStep === WizardStep.SUCCESS;
  const isBusy = isCreating || isConfirmingPlan;

  // Resume prop can arrive after first paint (bootstrap loading →
  // needs-onboarding). Adjust during render so a remount does not stick on Intent.
  if (resumeWorkspace && billingInApp && currentStep < WizardStep.PLAN) {
    setCurrentStep(WizardStep.PLAN);
  }
  if (resumeWorkspace && createdWorkspace == null) {
    setCreatedWorkspace(resumeWorkspace);
  }
  if (resumeWorkspace && !workspaceName) {
    setWorkspaceName(resumeWorkspace.name);
  }
  if (resumeWorkspace && !workspaceUsername && resumeWorkspace.username) {
    setWorkspaceUsername(resumeWorkspace.username);
  }

  const nativeResumeConfirmStarted = useRef(false);
  // Cap native resume: auto-confirm plan gate (no Stripe UI) then land on success.
  useEffect(() => {
    if (!skipPlanStep || !resumeWorkspace) return;
    if (nativeResumeConfirmStarted.current) return;
    nativeResumeConfirmStarted.current = true;
    let cancelled = false;
    void (async () => {
      setIsConfirmingPlan(true);
      try {
        await persistPlanConfirmed(resumeWorkspace.id);
        if (cancelled) return;
        setPlanOnboardingGateIncomplete(queryClient, resumeWorkspace, false);
        invalidatePlanOnboardingGate(queryClient, resumeWorkspace.id);
        setCreatedWorkspace(resumeWorkspace);
        setCurrentStep(WizardStep.SUCCESS);
      } catch (error) {
        console.error('Failed to auto-confirm plan on Cap:', error);
        nativeResumeConfirmStarted.current = false;
      } finally {
        if (!cancelled) setIsConfirmingPlan(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skipPlanStep, resumeWorkspace, queryClient]);

  useEffect(() => {
    if (!resumeWorkspace) return;
    let cancelled = false;
    void (async () => {
      try {
        const progress = await getGuidanceProgress(resumeWorkspace.id);
        if (cancelled) return;
        const slice = parseGetStartedSlice(progress?.checklists);
        if (slice.intent) setIntent(slice.intent);
      } catch {
        // Intent stays null; plan step still works.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resumeWorkspace]);

  const stepLabels = skipPlanStep
    ? [
        dict.onboarding.progress.focus,
        dict.onboarding.progress.details,
        dict.onboarding.progress.ready,
      ]
    : [
        dict.onboarding.progress.focus,
        dict.onboarding.progress.details,
        dict.onboarding.progress.plan,
        dict.onboarding.progress.ready,
      ];

  const displayStep =
    skipPlanStep && currentStep === WizardStep.SUCCESS
      ? 3
      : skipPlanStep && currentStep === WizardStep.PLAN
        ? 2
        : currentStep;

  const goForward = (to: WizardStep) => {
    setDirection(1);
    setCurrentStep(to);
  };

  const goBack = () => {
    if (currentStep <= WizardStep.INTENT || isSuccessStep) return;
    // Resume-to-plan: don't send users back into create (workspace exists).
    if (resumeWorkspace && currentStep === WizardStep.PLAN) return;
    setDirection(-1);
    setCurrentStep(currentStep - 1);
  };

  const validateWorkspaceDetails = (): boolean => {
    if (!workspaceName.trim()) {
      setNameError(dict.workspace.create.errors.invalidName);
      toast.error(dict.workspace.create.errors.invalidName);
      void triggerErrorHaptic();
      return false;
    }
    const trimmedUsername = workspaceUsername.trim();
    if (!trimmedUsername) {
      const msg =
        dict.workspace.create.errors.usernameRequired ||
        'Please enter a username';
      setUsernameError(msg);
      toast.error(msg);
      void triggerErrorHaptic();
      return false;
    }
    if (!/^[a-z0-9._-]+$/.test(trimmedUsername)) {
      const msg =
        dict.workspace.create.errors.usernameInvalidFormat ||
        'Username can only contain lowercase letters, numbers, dots, underscores and hyphens';
      setUsernameError(msg);
      toast.error(msg);
      void triggerErrorHaptic();
      return false;
    }
    if (trimmedUsername.length < 3 || trimmedUsername.length > 30) {
      const msg =
        trimmedUsername.length < 3
          ? dict.workspace.create.errors.usernameMinLength
          : dict.workspace.create.errors.usernameMaxLength;
      setUsernameError(msg);
      toast.error(msg);
      void triggerErrorHaptic();
      return false;
    }
    setNameError(null);
    setUsernameError(null);
    return true;
  };

  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim() || !workspaceUsername.trim() || !intent) return;
    if (!validateWorkspaceDetails()) return;

    setIsCreating(true);
    setCreateError(null);

    try {
      const response = await WorkspacesApi.create({
        body: {
          name: workspaceName.trim(),
          username: workspaceUsername.trim(),
          description: workspaceDescription.trim() || undefined,
          photo: workspacePhotoFileId
            ? { id: workspacePhotoFileId }
            : undefined,
          defaultLocale: appLocale === 'en' ? LocaleEnum.EN : LocaleEnum.DE,
        },
      });

      const createdWs = response.data;
      if (!createdWs) {
        throw new Error('empty create response');
      }

      setCreatedWorkspace(createdWs);
      await persistWorkspaceIntent(createdWs.id, intent);
      // Keep bootstrap on needs-onboarding while guidance refetch settles.
      setPlanOnboardingGateIncomplete(queryClient, createdWs, true);
      invalidatePlanOnboardingGate(queryClient, createdWs.id);

      // Zero-workspace onboarding must select immediately (no prior workspace).
      // Create-another must NOT — selecting an incomplete workspace flips
      // bootstrap to needs-onboarding, which overlays/redirects the in-shell
      // wizard and remounts it at Intent (workspace still created).
      if (mode === 'onboarding') {
        setWorkspace(createdWs);
      }

      void refetch();
      setIsCreating(false);
      void triggerSuccessHaptic();
      if (skipPlanStep) {
        await finishPlanConfirmed(createdWs);
      } else {
        goForward(WizardStep.PLAN);
      }
    } catch (error) {
      console.error('Failed to create workspace:', error);
      setIsCreating(false);
      const axiosError = error as {
        response?: { data?: { errors?: { username?: string } } };
      };
      const usernameErr = axiosError?.response?.data?.errors?.username;

      if (usernameErr === 'usernameAlreadyTaken') {
        const msg =
          dict.workspace.create.errors.usernameAlreadyTaken ||
          'This username is already taken';
        setUsernameError(msg);
        setCreateError(null);
        toast.error(msg);
      } else {
        const msg = dict.workspace.create.errors.createFailed;
        setCreateError(msg);
        toast.error(msg);
      }
      void triggerErrorHaptic();
    }
  };

  const finishPlanConfirmed = async (workspace: Workspace) => {
    await persistPlanConfirmed(workspace.id);
    // Optimistic clear so "Go to Home" cannot race a stale plan-gate query.
    setPlanOnboardingGateIncomplete(queryClient, workspace, false);
    invalidatePlanOnboardingGate(queryClient, workspace.id);
    void triggerSuccessHaptic();
    goForward(WizardStep.SUCCESS);
  };

  const handleConfirmPlan = async () => {
    const workspace = createdWorkspace;
    if (!workspace || !selectedPlan) {
      toast.error(dict.workspace.create.errors.selectPlan);
      void triggerErrorHaptic();
      return;
    }

    setIsConfirmingPlan(true);
    try {
      if (selectedPlan === WorkspacePlan.FREE) {
        await finishPlanConfirmed(workspace);
        return;
      }

      if (selectedPlan === WorkspacePlan.ENTERPRISE) {
        setEnterpriseOpen(true);
        await finishPlanConfirmed(workspace);
        return;
      }

      // Plus / Teams — start Stripe Checkout (trial when configured).
      // Do not mark planConfirmed until subscription mirrors; abandon → resume.
      // Cap native: web-only billing — confirm Free instead of opening Checkout.
      if (!canPurchaseBillingInApp()) {
        await finishPlanConfirmed(workspace);
        return;
      }
      try {
        await startCheckout.mutateAsync({
          plan: selectedPlan,
          billingCycle,
          workspaceId: workspace.id,
        });
      } catch {
        setPendingUpgrade(workspace.id, selectedPlan, billingCycle);
        void triggerErrorHaptic();
      }
    } catch (error) {
      console.error('Failed to confirm plan:', error);
      toast.error(dict.workspace.create.errors.createFailed);
      void triggerErrorHaptic();
    } finally {
      setIsConfirmingPlan(false);
    }
  };

  const handleNext = () => {
    switch (currentStep) {
      case WizardStep.INTENT:
        if (!intent) {
          toast.error(dict.onboarding.intent.selectRequired);
          void triggerErrorHaptic();
          return;
        }
        goForward(WizardStep.WORKSPACE);
        break;
      case WizardStep.WORKSPACE:
        void handleCreateWorkspace();
        break;
      case WizardStep.PLAN:
        void handleConfirmPlan();
        break;
      default:
        break;
    }
  };

  const handleDone = useCallback(() => {
    if (createdWorkspace) {
      onCreated(createdWorkspace);
    }
  }, [createdWorkspace, onCreated]);

  const isNextDisabled =
    (currentStep === WizardStep.INTENT && !intent) ||
    (currentStep === WizardStep.WORKSPACE &&
      (!workspaceName.trim() || !workspaceUsername.trim() || isCreating)) ||
    (currentStep === WizardStep.PLAN &&
      billingInApp &&
      (!selectedPlan || isConfirmingPlan));

  const nextLabel = (() => {
    if (currentStep === WizardStep.INTENT) return dict.onboarding.continue;
    if (currentStep === WizardStep.WORKSPACE) {
      return isCreating
        ? dict.onboarding.success.creating
        : dict.onboarding.createWorkspace;
    }
    if (currentStep === WizardStep.PLAN) {
      if (isConfirmingPlan) return dict.onboarding.plan.confirming;
      if (
        selectedPlan === WorkspacePlan.PLUS ||
        selectedPlan === WorkspacePlan.TEAMS
      ) {
        return dict.onboarding.plan.startTrial.replace(
          '{days}',
          String(TRIAL_DAYS),
        );
      }
      if (selectedPlan === WorkspacePlan.FREE) {
        return dict.onboarding.plan.confirm;
      }
      if (selectedPlan === WorkspacePlan.ENTERPRISE) {
        return dict.onboarding.continue;
      }
      return dict.onboarding.continue;
    }
    return dict.onboarding.continue;
  })();

  const showBack =
    currentStep === WizardStep.WORKSPACE ||
    (currentStep === WizardStep.PLAN && billingInApp && !resumeWorkspace);

  return (
    <>
      <WizardShell
        currentStep={displayStep}
        totalSteps={totalSteps}
        stepLabels={stepLabels}
        direction={direction}
        stepKey={currentStep}
        onBack={showBack ? goBack : undefined}
        onNext={isSuccessStep ? undefined : handleNext}
        nextLabel={nextLabel}
        nextDisabled={isNextDisabled}
        nextDisabledHint={
          currentStep === WizardStep.INTENT
            ? dict.onboarding.intentRequired
            : undefined
        }
        isLoading={isBusy}
        showBack={showBack}
        showFooter={!isSuccessStep}
        topRightAction={isSuccessStep ? undefined : topRightAction}
        variant={variant}
      >
        {currentStep === WizardStep.INTENT ? (
          <OnboardingIntentStep
            selected={intent}
            showGreeting={mode === 'onboarding'}
            onSelect={(next) => {
              if (next !== intent) void triggerSelectionHaptic();
              setIntent(next);
              stashPendingWorkspaceIntent(next);
              trackOnboardingIntentSelected({ intent: next, mode });
            }}
          />
        ) : null}

        {currentStep === WizardStep.WORKSPACE ? (
          <OnboardingWorkspaceStep
            mode={mode}
            name={workspaceName}
            onNameChange={(v) => {
              setWorkspaceName(v);
              if (v.trim() && nameError) setNameError(null);
              if (createError) setCreateError(null);
            }}
            username={workspaceUsername}
            onUsernameChange={(v) => {
              setWorkspaceUsername(v);
              if (v.trim() && usernameError) setUsernameError(null);
              if (createError) setCreateError(null);
            }}
            description={workspaceDescription}
            onDescriptionChange={setWorkspaceDescription}
            photoPreview={workspacePhotoPreview}
            onPhotoChange={(fileId, preview) => {
              setWorkspacePhotoFileId(fileId);
              setWorkspacePhotoPreview(preview);
            }}
            nameError={nameError}
            usernameError={usernameError}
            createError={createError}
            isCreating={isCreating}
          />
        ) : null}

        {currentStep === WizardStep.PLAN && billingInApp ? (
          <OnboardingPlanStep
            selectedPlan={selectedPlan}
            onPlanChange={(plan) => {
              if (plan !== selectedPlan) void triggerSelectionHaptic();
              setSelectedPlan(plan);
            }}
            billingCycle={billingCycle}
            onBillingCycleChange={(cycle) => {
              if (cycle !== billingCycle) void triggerSelectionHaptic();
              setBillingCycle(cycle);
            }}
          />
        ) : null}

        {currentStep === WizardStep.SUCCESS ? (
          <OnboardingSuccessStep
            mode={mode}
            workspaceName={createdWorkspace?.name ?? workspaceName}
            onGoToDashboard={handleDone}
            intent={intent}
            ctaDisabled={isConfirmingPlan}
            showNativePlanNotice={skipPlanStep}
          />
        ) : null}
      </WizardShell>

      <EnterpriseContactDialog
        open={enterpriseOpen}
        onOpenChange={setEnterpriseOpen}
      />
    </>
  );
}
